import { useState, useEffect, useRef } from 'react';
import { Send, Edit2, ChevronDown, Flower2, MessageCircle, Gem, Rocket, Heart, Share2, Zap, Gift, UserPlus, LogIn } from 'lucide-react';
import tiktokIcon from '../assets/icons/platform-tiktok.png';
import youtubeIcon from '../assets/icons/platform-youtube.png';
import twitchIcon from '../assets/icons/platform-twitch.png';
import { generateFromPattern } from '../utils/chatPatternParser';

interface LivestreamSimulatorProps {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  bridgeReady: boolean;
  chatFormat?: string;
  sdkVersion?: string;
}

interface Viewer {
  id: string;
  name: string;
  avatarUrl: string;
}

const PLATFORMS = [
  { id: 'tiktok', name: 'TikTok', icon: tiktokIcon },
  { id: 'youtube', name: 'YouTube', icon: youtubeIcon },
  { id: 'twitch', name: 'Twitch', icon: twitchIcon }
];

// List of random username prefixes
const USERNAME_PREFIXES = [
  'gamer', 'player', 'pro', 'ninja', 'shadow', 'storm', 'fire', 'ice', 'thunder', 'dragon',
  'wolf', 'tiger', 'bear', 'eagle', 'hawk', 'viper', 'phantom', 'ghost', 'blade', 'arrow',
  'star', 'moon', 'sun', 'sky', 'ocean', 'river', 'mountain', 'forest', 'desert', 'valley'
];

// Helper to generate random user with portrait avatar (1-20)
function generateRandomUser(): { name: string; avatarUrl: string } {
  const gender = Math.random() > 0.5 ? 'men' : 'women';
  const avatarIndex = Math.floor(Math.random() * 20) + 1;
  const prefix = USERNAME_PREFIXES[Math.floor(Math.random() * USERNAME_PREFIXES.length)];
  const randomNum = Math.floor(Math.random() * 999);
  
  return {
    name: `${prefix}${randomNum}`,
    avatarUrl: `https://randomuser.me/api/portraits/${gender}/${avatarIndex}.jpg`
  };
}

// Platform-specific gift configurations
const PLATFORM_GIFTS = {
  tiktok: [
    { id: 'rose', name: 'Rose', icon: Flower2, emoji: '🌹', count: 1 },
    { id: 'gg', name: 'GG', icon: MessageCircle, emoji: '💬', count: 1 },
    { id: 'diamond', name: 'Diamond', icon: Gem, emoji: '💎', count: 5 },
    { id: 'plane', name: 'Rocket', icon: Rocket, emoji: '🚀', count: 100 }
  ],
  youtube: [], // YouTube only has likes, no gifts in action row
  twitch: [
    { id: 'bits1', name: 'Bits', icon: Zap, emoji: '⚡', count: 1 },
    { id: 'bits100', name: 'Bits', icon: Zap, emoji: '⚡', count: 100 },
    { id: 'bits1000', name: 'Bits', icon: Zap, emoji: '⚡', count: 1000 }
  ]
};

interface ChatMessage {
  id: string;
  username: string;
  avatarUrl: string;
  text: string;
  timestamp: Date;
  type: 'chat' | 'gift' | 'like' | 'share' | 'follow' | 'join';
}

// Generic chat messages pool
const GENERIC_CHATS = [
  'Hello!', 'Hi everyone', 'Nice game', 'This is fun',
  'Let\'s go!', 'Amazing', 'Cool!', 'Awesome',
  'Great stream', 'Love this', 'Good one', 'Nice',
  'GG', 'Woah!', 'Interesting', 'LOL',
  'Keep it up', 'So good', 'First!', 'Hype!',
  'What a game', 'Epic', 'Let me try', 'I want to play',
  'How do I play?', 'OMG', 'No way', 'Sick!',
  'Crazy', 'Wild', 'Insane', 'Poggers',
  'Nice move', 'Well played', 'Not bad', 'Entertaining'
];

// Generate consistent color for username
const getUsernameColor = (username: string): string => {
  const colors = [
    'text-red-400',
    'text-orange-400',
    'text-yellow-400',
    'text-green-400',
    'text-blue-400',
    'text-indigo-400',
    'text-purple-400',
    'text-pink-400',
    'text-cyan-400',
    'text-lime-400',
  ];
  
  // Generate hash from username
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

export default function LivestreamSimulator({ iframeRef, bridgeReady, chatFormat = 'generic', sdkVersion }: LivestreamSimulatorProps) {
  const [selectedPlatform, setSelectedPlatform] = useState('tiktok');
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [selectedViewerId, setSelectedViewerId] = useState<string>('');
  const [comment, setComment] = useState('');
  const [isEditingViewer, setIsEditingViewer] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [showViewerDropdown, setShowViewerDropdown] = useState(false);
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  
  // TikTok gift accumulation state
  const [pendingGift, setPendingGift] = useState<{ id: string; count: number; gift: any; animationKey: number } | null>(null);
  const giftTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const giftSentRef = useRef<boolean>(false); // Track if gift was already sent to prevent StrictMode double-call
  
  // Auto-chat state
  const [isAutoChatEnabled, setIsAutoChatEnabled] = useState(false);
  const [autoChatSpeed, setAutoChatSpeed] = useState(50); // 0-100 scale for slider (maps to 0-10 m/s logarithmically)
  const autoChatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const statsIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Auto-interaction rates
  const [likeRate, setLikeRate] = useState(0); // 0-100 scale
  const [shareRate, setShareRate] = useState(0); // 0-100 scale
  const [followRate, setFollowRate] = useState(0); // 0-100 scale
  const [joinRate, setJoinRate] = useState(0); // 0-100 scale
  const likeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const shareIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const followIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const joinIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Toast notification state for likes/shares
  interface ToastNotification {
    id: string;
    username: string;
    avatarUrl: string;
    type: 'like' | 'share';
  }
  const [currentToast, setCurrentToast] = useState<ToastNotification | null>(null);
  const [toastQueue, setToastQueue] = useState<ToastNotification[]>([]);
  
  // Gift panel visibility (TikTok)
  const [showGiftPanel, setShowGiftPanel] = useState(false);
  
  // Auto-chat speed controls visibility
  const [showSpeedControls, setShowSpeedControls] = useState(false);
  
  // Stats dropdown visibility
  const [showStatsDropdown, setShowStatsDropdown] = useState(false);
  
  // Stats values
  const [statsLikes, setStatsLikes] = useState(0);
  const [statsViewers, setStatsViewers] = useState(0);
  const [statsShares, setStatsShares] = useState(0);
  const [statsGifts, setStatsGifts] = useState(0);
  const [statsComments, setStatsComments] = useState(0);
  const [statsFollowers, setStatsFollowers] = useState(0);
  
  // Convert slider value (0-100) to messages per second (0-10) logarithmically
  const getMessagesPerSecond = (sliderValue: number): number => {
    // Map 0-100 to 0-10 logarithmically
    // When slider is 0, return 0 (pause auto-chat)
    if (sliderValue === 0) return 0;
    // Formula for 1-100: 0.1 * 10^((sliderValue-1)/99 * 2)
    return 0.1 * Math.pow(10, ((sliderValue - 1) / 99) * 2);
  };
  
  const avatarDropdownRef = useRef<HTMLDivElement>(null);
  const viewerDropdownRef = useRef<HTMLDivElement>(null);
  const platformDropdownRef = useRef<HTMLDivElement>(null);
  const speedControlsDropdownRef = useRef<HTMLDivElement>(null);
  const statsDropdownRef = useRef<HTMLDivElement>(null);
  const chatFeedRef = useRef<HTMLDivElement>(null);

  const currentViewer = viewers.find(v => v.id === selectedViewerId);
  const currentGifts = PLATFORM_GIFTS[selectedPlatform as keyof typeof PLATFORM_GIFTS] || [];

  // Initialize viewers with random users on mount
  useEffect(() => {
    const initialViewers: Viewer[] = [];
    
    // Generate 5 random users
    for (let i = 0; i < 5; i++) {
      const userData = generateRandomUser();
      initialViewers.push({
        id: `viewer-${Date.now()}-${i}`,
        name: userData.name,
        avatarUrl: userData.avatarUrl
      });
    }
    
    setViewers(initialViewers);
    if (initialViewers.length > 0) {
      setSelectedViewerId(initialViewers[0].id);
    }
  }, []);

  // Close avatar dropdown when clicking outside (unless in edit mode)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (avatarDropdownRef.current && !avatarDropdownRef.current.contains(event.target as Node)) {
        // Don't close if in edit mode
        if (!isEditingViewer) {
          setShowAvatarSelector(false);
        }
      }
    };

    if (showAvatarSelector) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAvatarSelector, isEditingViewer]);

  // Close viewer dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (viewerDropdownRef.current && !viewerDropdownRef.current.contains(event.target as Node)) {
        setShowViewerDropdown(false);
      }
    };

    if (showViewerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showViewerDropdown]);

  // Close platform dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (platformDropdownRef.current && !platformDropdownRef.current.contains(event.target as Node)) {
        setShowPlatformDropdown(false);
      }
    };

    if (showPlatformDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPlatformDropdown]);

  // Close speed controls dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (speedControlsDropdownRef.current && !speedControlsDropdownRef.current.contains(event.target as Node)) {
        setShowSpeedControls(false);
      }
    };

    if (showSpeedControls) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSpeedControls]);

  // Close stats dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statsDropdownRef.current && !statsDropdownRef.current.contains(event.target as Node)) {
        setShowStatsDropdown(false);
      }
    };

    if (showStatsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatsDropdown]);

  // Auto-scroll chat to bottom when new messages arrive or on mount
  useEffect(() => {
    if (chatFeedRef.current) {
      chatFeedRef.current.scrollTop = chatFeedRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Initial scroll to bottom on mount
  useEffect(() => {
    if (chatFeedRef.current) {
      chatFeedRef.current.scrollTop = chatFeedRef.current.scrollHeight;
    }
  }, []);

  // Cleanup gift timer on unmount
  useEffect(() => {
    return () => {
      if (giftTimeoutRef.current) {
        clearTimeout(giftTimeoutRef.current);
      }
    };
  }, []);

  // Cleanup stats interval on unmount
  useEffect(() => {
    return () => {
      if (statsIntervalRef.current) {
        clearInterval(statsIntervalRef.current);
      }
    };
  }, []);

  // Cleanup interaction intervals on unmount
  useEffect(() => {
    return () => {
      if (likeIntervalRef.current) clearInterval(likeIntervalRef.current);
      if (shareIntervalRef.current) clearInterval(shareIntervalRef.current);
      if (followIntervalRef.current) clearInterval(followIntervalRef.current);
      if (joinIntervalRef.current) clearInterval(joinIntervalRef.current);
    };
  }, []);

  // Toast notification processing - show next toast from queue
  useEffect(() => {
    // If there's already a toast showing or no queue, do nothing
    if (currentToast || toastQueue.length === 0) return;

    // Get next toast from queue
    const nextToast = toastQueue[0];
    setCurrentToast(nextToast);
    setToastQueue(prev => prev.slice(1));
  }, [currentToast, toastQueue]);

  // Handle toast timeout separately
  useEffect(() => {
    if (!currentToast) return;

    // Determine display duration: 3s if no queue, 1s if others are waiting
    const duration = toastQueue.length > 0 ? 1000 : 3000;

    // Clear after duration
    const timeout = setTimeout(() => {
      setCurrentToast(null);
    }, duration);

    return () => {
      clearTimeout(timeout);
    };
  }, [currentToast, toastQueue.length]);

  // Add toast notification
  const addToast = (username: string, avatarUrl: string, type: 'like' | 'share') => {
    const toast: ToastNotification = {
      id: `toast-${Date.now()}-${Math.random()}`,
      username,
      avatarUrl,
      type
    };
    setToastQueue(prev => [...prev, toast]);
  };

  // Auto-chat interval logic
  useEffect(() => {
    if (!isAutoChatEnabled || !bridgeReady || viewers.length === 0) {
      if (autoChatIntervalRef.current) {
        clearTimeout(autoChatIntervalRef.current);
        autoChatIntervalRef.current = null;
      }
      return;
    }

    // Convert slider value to messages per second, then to base delay in ms
    const messagesPerSecond = getMessagesPerSecond(autoChatSpeed);
    
    // If speed is 0, pause auto-chat
    if (messagesPerSecond === 0) {
      if (autoChatIntervalRef.current) {
        clearTimeout(autoChatIntervalRef.current);
        autoChatIntervalRef.current = null;
      }
      return;
    }
    
    const baseDelay = 1000 / messagesPerSecond;
    
    const scheduleNextChat = () => {
      // Pick a random viewer from the list
      const randomViewer = viewers[Math.floor(Math.random() * viewers.length)];
      
      // 50% chance: gameplay chat, 50% generic chat
      const useGameplayChat = chatFormat && 
                             chatFormat !== 'generic' && 
                             Math.random() > 0.5;
      
      let message: string;
      if (useGameplayChat) {
        message = generateFromPattern(chatFormat);
      } else {
        message = GENERIC_CHATS[Math.floor(Math.random() * GENERIC_CHATS.length)];
      }
      
      // Send chat
      const beemiDev = getBeemiDev();
      if (beemiDev) {
        beemiDev.streams.simulateChat(
          randomViewer.name,
          message,
          selectedPlatform,
          randomViewer.avatarUrl
        );
        addChatMessage(randomViewer.name, randomViewer.avatarUrl, message, 'chat');
      }
      
      // Schedule next chat with ±20% randomization
      const randomizedDelay = baseDelay * (0.8 + Math.random() * 0.4);
      autoChatIntervalRef.current = setTimeout(scheduleNextChat, randomizedDelay);
    };
    
    // Clear existing timeout if any (for speed changes)
    if (autoChatIntervalRef.current) {
      clearTimeout(autoChatIntervalRef.current);
    }
    
    // Start the auto-chat loop with initial randomized delay
    const initialDelay = baseDelay * (0.8 + Math.random() * 0.4);
    autoChatIntervalRef.current = setTimeout(scheduleNextChat, initialDelay);
    
    return () => {
      if (autoChatIntervalRef.current) {
        clearTimeout(autoChatIntervalRef.current);
      }
    };
  }, [isAutoChatEnabled, autoChatSpeed, bridgeReady, viewers, chatFormat, selectedPlatform]);

  // Broadcast stats every 3 seconds
  useEffect(() => {
    if (!bridgeReady) {
      if (statsIntervalRef.current) {
        clearInterval(statsIntervalRef.current);
        statsIntervalRef.current = null;
      }
      return;
    }

    // Send stats immediately on bridge ready
    const beemiDev = getBeemiDev();
    if (beemiDev) {
      beemiDev.streams.simulateStats({
        likes: statsLikes,
        viewers: statsViewers,
        shares: statsShares,
        gifts: statsGifts,
        comments: statsComments,
        followers: statsFollowers,
        platform: selectedPlatform
      });
    }

    // Set up 3-second interval
    statsIntervalRef.current = setInterval(() => {
      const beemiDev = getBeemiDev();
      if (beemiDev) {
        beemiDev.streams.simulateStats({
          likes: statsLikes,
          viewers: statsViewers,
          shares: statsShares,
          gifts: statsGifts,
          comments: statsComments,
          followers: statsFollowers,
          platform: selectedPlatform
        });
      }
    }, 3000);

    return () => {
      if (statsIntervalRef.current) {
        clearInterval(statsIntervalRef.current);
      }
    };
  }, [bridgeReady, statsLikes, statsViewers, statsShares, statsGifts, statsComments, statsFollowers, selectedPlatform]);

  // Auto-like interval
  useEffect(() => {
    if (!bridgeReady || !isAutoChatEnabled || likeRate === 0 || viewers.length === 0) {
      if (likeIntervalRef.current) {
        clearInterval(likeIntervalRef.current);
        likeIntervalRef.current = null;
      }
      return;
    }

    const actionsPerSecond = getMessagesPerSecond(likeRate);
    const baseDelay = 1000 / actionsPerSecond;

    const scheduleNextLike = () => {
      handleLike();
      const randomizedDelay = baseDelay * (0.8 + Math.random() * 0.4);
      likeIntervalRef.current = setTimeout(scheduleNextLike, randomizedDelay);
    };

    const initialDelay = baseDelay * (0.8 + Math.random() * 0.4);
    likeIntervalRef.current = setTimeout(scheduleNextLike, initialDelay);

    return () => {
      if (likeIntervalRef.current) clearTimeout(likeIntervalRef.current);
    };
  }, [bridgeReady, isAutoChatEnabled, likeRate, viewers, selectedPlatform]);

  // Auto-share interval
  useEffect(() => {
    if (!bridgeReady || !isAutoChatEnabled || shareRate === 0 || viewers.length === 0) {
      if (shareIntervalRef.current) {
        clearInterval(shareIntervalRef.current);
        shareIntervalRef.current = null;
      }
      return;
    }

    const actionsPerSecond = getMessagesPerSecond(shareRate);
    const baseDelay = 1000 / actionsPerSecond;

    const scheduleNextShare = () => {
      handleShare();
      const randomizedDelay = baseDelay * (0.8 + Math.random() * 0.4);
      shareIntervalRef.current = setTimeout(scheduleNextShare, randomizedDelay);
    };

    const initialDelay = baseDelay * (0.8 + Math.random() * 0.4);
    shareIntervalRef.current = setTimeout(scheduleNextShare, initialDelay);

    return () => {
      if (shareIntervalRef.current) clearTimeout(shareIntervalRef.current);
    };
  }, [bridgeReady, isAutoChatEnabled, shareRate, viewers, selectedPlatform]);

  // Auto-follow interval
  useEffect(() => {
    if (!bridgeReady || !isAutoChatEnabled || followRate === 0 || viewers.length === 0) {
      if (followIntervalRef.current) {
        clearInterval(followIntervalRef.current);
        followIntervalRef.current = null;
      }
      return;
    }

    const actionsPerSecond = getMessagesPerSecond(followRate);
    const baseDelay = 1000 / actionsPerSecond;

    const scheduleNextFollow = () => {
      handleFollow();
      const randomizedDelay = baseDelay * (0.8 + Math.random() * 0.4);
      followIntervalRef.current = setTimeout(scheduleNextFollow, randomizedDelay);
    };

    const initialDelay = baseDelay * (0.8 + Math.random() * 0.4);
    followIntervalRef.current = setTimeout(scheduleNextFollow, initialDelay);

    return () => {
      if (followIntervalRef.current) clearTimeout(followIntervalRef.current);
    };
  }, [bridgeReady, isAutoChatEnabled, followRate, viewers, selectedPlatform]);

  // Auto-join interval
  useEffect(() => {
    if (!bridgeReady || !isAutoChatEnabled || joinRate === 0 || viewers.length === 0) {
      if (joinIntervalRef.current) {
        clearInterval(joinIntervalRef.current);
        joinIntervalRef.current = null;
      }
      return;
    }

    const actionsPerSecond = getMessagesPerSecond(joinRate);
    const baseDelay = 1000 / actionsPerSecond;

    const scheduleNextJoin = () => {
      handleJoin();
      const randomizedDelay = baseDelay * (0.8 + Math.random() * 0.4);
      joinIntervalRef.current = setTimeout(scheduleNextJoin, randomizedDelay);
    };

    const initialDelay = baseDelay * (0.8 + Math.random() * 0.4);
    joinIntervalRef.current = setTimeout(scheduleNextJoin, initialDelay);

    return () => {
      if (joinIntervalRef.current) clearTimeout(joinIntervalRef.current);
    };
  }, [bridgeReady, isAutoChatEnabled, joinRate, viewers, selectedPlatform]);

  const getBeemiDev = () => {
    const iframeWindow = iframeRef.current?.contentWindow as any;
    return iframeWindow?.beemiDev;
  };

  const addChatMessage = (username: string, avatarUrl: string, text: string, type: ChatMessage['type']) => {
    const newMessage: ChatMessage = {
      id: `msg-${Date.now()}-${Math.random()}`,
      username,
      avatarUrl,
      text,
      timestamp: new Date(),
      type
    };
    setChatMessages(prev => {
      const updated = [...prev, newMessage];
      // Keep only the last 30 messages
      return updated.slice(-30);
    });
  };

  const handleAddViewer = () => {
    const userData = generateRandomUser();
    const newViewer: Viewer = {
      id: `viewer-${Date.now()}`,
      name: userData.name,
      avatarUrl: userData.avatarUrl
    };
    
    setViewers([...viewers, newViewer]);
    setSelectedViewerId(newViewer.id);
    setShowViewerDropdown(false);
  };

  const handleEditViewer = () => {
    if (!currentViewer) return;
    setEditedName(currentViewer.name);
    setIsEditingViewer(true);
    setShowAvatarSelector(true); // Show avatar selector when entering edit mode
  };

  const handleSaveViewerName = () => {
    if (!currentViewer || !editedName.trim()) return;
    
    setViewers(viewers.map(v => 
      v.id === currentViewer.id ? { ...v, name: editedName.trim() } : v
    ));
    setIsEditingViewer(false);
    setEditedName('');
    setShowAvatarSelector(false); // Close avatar selector when saving
  };

  const handleCancelEdit = () => {
    setIsEditingViewer(false);
    setEditedName('');
    setShowAvatarSelector(false); // Close avatar selector when canceling
  };

  const handleEditKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveViewerName();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleSelectViewer = (viewerId: string) => {
    setSelectedViewerId(viewerId);
    setShowViewerDropdown(false);
  };

  const handleSelectAvatar = (avatarUrl: string) => {
    if (!currentViewer) return;
    
    setViewers(viewers.map(v => 
      v.id === currentViewer.id ? { ...v, avatarUrl } : v
    ));
    // Keep selector open in edit mode, close only when not editing
    if (!isEditingViewer) {
      setShowAvatarSelector(false);
    }
  };

  const handleSendComment = () => {
    if (!comment.trim() || !bridgeReady || !currentViewer) return;
    
    const beemiDev = getBeemiDev();
    if (!beemiDev) {
      console.warn('beemiDev not available');
      return;
    }
    
    beemiDev.streams.simulateChat(
      currentViewer.name,
      comment,
      selectedPlatform,
      currentViewer.avatarUrl
    );
    
    // Add message to chat feed
    addChatMessage(currentViewer.name, currentViewer.avatarUrl, comment, 'chat');
    
    // Increment comments counter
    setStatsComments(prev => prev + 1);
    
    console.log(`💬 Sent comment: ${comment} (${selectedPlatform})`);
    setComment('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendComment();
    }
  };

  // Send accumulated gift (called after timer expires)
  const sendAccumulatedGift = (giftData: { id: string; count: number; gift: any; animationKey: number }) => {
    if (!bridgeReady || !currentViewer) return;
    
    // Prevent double-call from React StrictMode
    if (giftSentRef.current) return;
    giftSentRef.current = true;
    
    const beemiDev = getBeemiDev();
    if (!beemiDev) {
      giftSentRef.current = false; // Reset if beemiDev not available
      return;
    }
    
    // Send gift with accumulated count and coin value (pass count as 4th parameter, value as 5th)
    beemiDev.streams.simulateGift(
      selectedPlatform,
      giftData.gift.name,
      giftData.gift.emoji,
      giftData.count,
      giftData.gift.count  // The 'count' property in gift config is actually the coin value
    );
    
    // Add message to chat feed
    const countText = giftData.count > 1 ? ` x${giftData.count}` : '';
    addChatMessage(
      currentViewer.name, 
      currentViewer.avatarUrl, 
      `sent ${giftData.gift.emoji} ${giftData.gift.name}${countText}`, 
      'gift'
    );
    
    console.log(`🎁 Sent ${giftData.count}x ${giftData.gift.name} (${selectedPlatform})`);
    
    // Increment gifts counter by the accumulated count
    setStatsGifts(prev => prev + giftData.count);
    
    // Clear pending gift
    setPendingGift(null);
  };

  const handleGiftClick = (gift: typeof currentGifts[0]) => {
    if (!bridgeReady || !currentViewer) return;
    
    // Clear existing timer if any
    if (giftTimeoutRef.current) {
      clearTimeout(giftTimeoutRef.current);
    }
    
    // Check if clicking the same gift or a different one
    if (pendingGift && pendingGift.id === gift.id) {
      // Same gift - accumulate count and reset animation
      setPendingGift({
        ...pendingGift,
        count: pendingGift.count + 1,
        animationKey: Date.now() // New key to restart animation
      });
    } else {
      // Different gift or first click - send previous if exists, start new
      if (pendingGift) {
        sendAccumulatedGift(pendingGift);
      }
      
      setPendingGift({
        id: gift.id,
        count: 1,
        gift: gift,
        animationKey: Date.now()
      });
    }
    
    // Set timer to send after 1.5 seconds
    giftTimeoutRef.current = setTimeout(() => {
      giftSentRef.current = false; // Reset flag before sending
      setPendingGift(current => {
        if (current) {
          sendAccumulatedGift(current);
        }
        return null;
      });
    }, 1500);
  };

  const handleLike = () => {
    if (!bridgeReady || !currentViewer) return;
    
    const beemiDev = getBeemiDev();
    if (!beemiDev) return;
    
    beemiDev.streams.simulateLike(selectedPlatform);
    
    // Show as toast instead of chat message
    addToast(currentViewer.name, currentViewer.avatarUrl, 'like');
    
    // Increment likes counter
    setStatsLikes(prev => prev + 1);
    
    console.log(`❤️ Sent like (${selectedPlatform})`);
  };

  const handleShare = () => {
    if (!bridgeReady || !currentViewer) return;
    
    const beemiDev = getBeemiDev();
    if (!beemiDev) return;
    
    beemiDev.streams.simulateShare(selectedPlatform);
    
    // Show as toast instead of chat message
    addToast(currentViewer.name, currentViewer.avatarUrl, 'share');
    
    // Increment shares counter
    setStatsShares(prev => prev + 1);
    
    console.log(`➜ Sent share (${selectedPlatform})`);
  };

  const handleJoin = () => {
    if (!bridgeReady || !currentViewer) return;
    
    const beemiDev = getBeemiDev();
    if (!beemiDev) return;
    
    beemiDev.streams.simulateJoin(currentViewer.name, selectedPlatform);
    
    // Add message to chat feed
    addChatMessage(currentViewer.name, currentViewer.avatarUrl, 'joined the live', 'join');
    
    // Increment viewers counter
    setStatsViewers(prev => prev + 1);
    
    console.log(`👋 Sent join (${selectedPlatform})`);
  };

  const handleFollow = () => {
    if (!bridgeReady || !currentViewer) return;
    
    const beemiDev = getBeemiDev();
    if (!beemiDev) return;
    
    beemiDev.streams.simulateFollow(currentViewer.name, selectedPlatform);
    
    // Add message to chat feed
    addChatMessage(currentViewer.name, currentViewer.avatarUrl, 'followed', 'follow');
    
    // Increment followers counter
    setStatsFollowers(prev => prev + 1);
    
    console.log(`➕ Sent follow (${selectedPlatform})`);
  };

  return (
    <>
      {/* CSS for gift timer animation and toast */}
      <style>{`
        @keyframes giftTimerSpin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes giftTimerProgress {
          from {
            stroke-dashoffset: 0;
          }
          to {
            stroke-dashoffset: 301.59;
          }
        }
        
        @keyframes toastSlideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes toastSlideOut {
          from {
            opacity: 1;
            transform: translateY(0);
          }
          to {
            opacity: 0;
            transform: translateY(-10px);
          }
        }
        
        .toast-enter {
          animation: toastSlideIn 0.2s ease-out forwards;
        }
        
        .toast-exit {
          animation: toastSlideOut 0.2s ease-in forwards;
        }
      `}</style>
      
      <div className="h-full flex flex-col bg-gray-100 dark:bg-gray-800">
      {/* SDK Status */}
      <div className="flex items-center justify-center bg-gray-200/50 dark:bg-gray-900/50 gap-1.5 px-3 py-1.5">
        <div className={`w-1.5 h-1.5 rounded-full ${bridgeReady ? 'bg-green-500' : 'bg-yellow-500'}`} />
        <span className={`text-[10px] font-medium ${bridgeReady ? 'text-green-400' : 'text-yellow-400'}`}>
          {bridgeReady ? 'Connected' : 'Connecting...'}
          {bridgeReady && sdkVersion && ` · v${sdkVersion}`}
        </span>
      </div>

      {/* Header with Platform and Viewer Dropdowns */}
      <div className="flex items-center gap-2 p-3">
        {/* Platform Dropdown */}
        <div className="relative" ref={platformDropdownRef}>
          <button
            onClick={() => setShowPlatformDropdown(!showPlatformDropdown)}
            disabled={!bridgeReady}
            className="flex items-center gap-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 rounded-lg cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-10"
          >
            <img 
              src={PLATFORMS.find(p => p.id === selectedPlatform)?.icon} 
              alt={PLATFORMS.find(p => p.id === selectedPlatform)?.name}
              className="w-6 h-6 object-contain [filter:brightness(0.1)_saturate(0)] dark:[filter:none]"
            />
            <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </button>
          
          {/* Platform List Dropdown */}
          {showPlatformDropdown && (
            <div className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-300 dark:border-gray-600 z-[60] min-w-[120px]">
              {PLATFORMS.map(platform => (
                <button
                  key={platform.id}
                  onClick={() => {
                    setSelectedPlatform(platform.id);
                    setShowPlatformDropdown(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                    platform.id === selectedPlatform ? 'bg-gray-50 dark:bg-gray-700' : ''
                  }`}
                >
                  <img 
                    src={platform.icon} 
                    alt={platform.name}
                    className="w-6 h-6 object-contain flex-shrink-0 [filter:brightness(0.1)_saturate(0)] dark:[filter:none]"
                  />
                  <span className="text-gray-900 dark:text-white text-sm">{platform.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Custom Viewer Dropdown with Avatar */}
        <div className="relative flex-1" ref={viewerDropdownRef}>
          {isEditingViewer ? (
            // Inline Edit Mode
            <div className="flex items-center gap-2 h-10 min-w-0">
              {currentViewer && (
                <img 
                  src={currentViewer.avatarUrl} 
                  alt={currentViewer.name}
                  className="w-6 h-6 rounded-full border-2 border-primary-500 flex-shrink-0"
                />
              )}
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleEditKeyPress}
                onBlur={handleCancelEdit}
                placeholder="Enter username..."
                autoFocus
                className="flex-1 min-w-0 bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 border border-primary-500 h-10"
              />
            </div>
          ) : (
            // Custom Dropdown Mode
            <>
              {/* Selected Viewer Display */}
              <button
                onClick={() => setShowViewerDropdown(!showViewerDropdown)}
                disabled={!bridgeReady}
                className="w-full flex items-center gap-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 pr-16 rounded-lg cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-10"
              >
                {currentViewer && (
                  <>
                    <img 
                      src={currentViewer.avatarUrl} 
                      alt={currentViewer.name}
                      className="w-6 h-6 rounded-full border border-gray-600 flex-shrink-0"
                    />
                    <span className="flex-1 text-left truncate">{currentViewer.name}</span>
                  </>
                )}
              </button>
              
              {/* Edit Icon - Only visible when a viewer is selected */}
              {currentViewer && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditViewer();
                  }}
                  disabled={!bridgeReady}
                  className="absolute right-8 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed z-10"
                  title="Edit viewer name"
                >
                  <Edit2 className="w-3.5 h-3.5 text-gray-400" />
                </button>
              )}
              
              {/* Dropdown Icon */}
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600 dark:text-gray-400 w-4 h-4" />
              
              {/* Viewer List Dropdown */}
              {showViewerDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-300 dark:border-gray-600 z-[60] max-h-80 overflow-y-auto">
                  {viewers.map(viewer => (
                    <button
                      key={viewer.id}
                      onClick={() => handleSelectViewer(viewer.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                        viewer.id === selectedViewerId ? 'bg-gray-50 dark:bg-gray-700' : ''
                      }`}
                    >
                      <img 
                        src={viewer.avatarUrl} 
                        alt={viewer.name}
                        className="w-6 h-6 rounded-full border border-gray-300 dark:border-gray-600 flex-shrink-0"
                      />
                      <span className="text-gray-900 dark:text-white text-sm flex-1 text-left">{viewer.name}</span>
                    </button>
                  ))}
                  
                  {/* Add Viewer Button */}
                  <button
                    onClick={handleAddViewer}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <span className="text-sm font-semibold text-primary-400">+ Add Viewer</span>
                  </button>
                </div>
              )}
            </>
          )}
          
          {/* Avatar Selector Dropdown - Available in both edit and normal mode */}
          {showAvatarSelector && (
            <div className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-800 rounded-lg p-3 shadow-xl border border-gray-300 dark:border-gray-600 z-[60] w-64" ref={avatarDropdownRef}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-gray-900 dark:text-white text-sm font-semibold">
                  Select Avatar
                </p>
                {!isEditingViewer && (
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent input blur
                      setShowAvatarSelector(false);
                    }}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-sm"
                  >
                    ✕
                  </button>
                )}
              </div>
              
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {/* Women Avatars */}
                <div>
                  <p className="text-gray-600 dark:text-gray-400 text-xs mb-1.5">Women</p>
                  <div className="grid grid-cols-5 gap-1.5">
                    {Array.from({ length: 20 }, (_, i) => i + 1).map(i => (
                      <button
                        key={`women-${i}`}
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent input blur
                          handleSelectAvatar(`https://randomuser.me/api/portraits/women/${i}.jpg`);
                        }}
                        className="w-10 h-10 rounded-full border-2 border-gray-600 hover:border-primary-500 transition-colors overflow-hidden"
                      >
                        <img
                          src={`https://randomuser.me/api/portraits/women/${i}.jpg`}
                          alt={`Avatar ${i}`}
                          className="w-full h-full"
                        />
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Men Avatars */}
                <div>
                  <p className="text-gray-600 dark:text-gray-400 text-xs mb-1.5">Men</p>
                  <div className="grid grid-cols-5 gap-1.5">
                    {Array.from({ length: 20 }, (_, i) => i + 1).map(i => (
                      <button
                        key={`men-${i}`}
                        onMouseDown={(e) => {
                          e.preventDefault(); // Prevent input blur
                          handleSelectAvatar(`https://randomuser.me/api/portraits/men/${i}.jpg`);
                        }}
                        className="w-10 h-10 rounded-full border-2 border-gray-600 hover:border-primary-500 transition-colors overflow-hidden"
                      >
                        <img
                          src={`https://randomuser.me/api/portraits/men/${i}.jpg`}
                          alt={`Avatar ${i}`}
                          className="w-full h-full"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Auto-Chat Controls */}
      <div className="px-3 pb-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-shrink-0" ref={speedControlsDropdownRef}>
            {/* Combined Toggle and Dropdown Button */}
            <div className="flex items-center bg-gray-200 dark:bg-gray-700 rounded-lg h-8 overflow-hidden">
              {/* Toggle Button */}
              <button
                onClick={() => setIsAutoChatEnabled(!isAutoChatEnabled)}
                disabled={!bridgeReady}
                title={isAutoChatEnabled 
                  ? "Auto-chat is sending mixed generic and gameplay-specific messages. Click to disable." 
                  : "Enable auto-chat to automatically send chat messages from viewers. Messages will be a mix of generic greetings and gameplay-specific inputs."}
                className={`px-3 py-2 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-full ${
                  isAutoChatEnabled 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                Auto
              </button>
              
              {/* Separator */}
              <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />
              
              {/* Dropdown Toggle */}
              <button
                onClick={() => setShowSpeedControls(!showSpeedControls)}
                disabled={!bridgeReady}
                title="Auto-chat settings"
                className="px-2 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-full hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                <ChevronDown className={`w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform ${
                  showSpeedControls ? 'rotate-180' : ''
                }`} />
              </button>
            </div>
            
            {/* Auto-Chat Controls Dropdown */}
            {showSpeedControls && (
              <div className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-300 dark:border-gray-600 z-[60] p-3 min-w-[240px] max-h-[400px] overflow-y-auto">
                <div className="flex flex-col gap-3">
                  {/* Message Speed */}
                  <div className={`flex flex-col gap-2 ${!isAutoChatEnabled ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">Message Rate</span>
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {getMessagesPerSecond(autoChatSpeed).toFixed(1)}/s
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0">0</span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={autoChatSpeed}
                        onChange={(e) => setAutoChatSpeed(Number(e.target.value))}
                        disabled={!bridgeReady || !isAutoChatEnabled}
                        className="flex-1 h-2 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
                        style={{
                          accentColor: '#10b981'
                        }}
                        title={`Messages per second: ${getMessagesPerSecond(autoChatSpeed).toFixed(1)}`}
                      />
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0">10</span>
                    </div>
                  </div>

                  {/* Like Rate */}
                  <div className={`flex flex-col gap-2 ${!isAutoChatEnabled ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">Like Rate</span>
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {getMessagesPerSecond(likeRate).toFixed(1)}/s
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0">0</span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={likeRate}
                        onChange={(e) => setLikeRate(Number(e.target.value))}
                        disabled={!bridgeReady || !isAutoChatEnabled}
                        className="flex-1 h-2 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
                        style={{
                          accentColor: '#10b981'
                        }}
                        title={`Likes per second: ${getMessagesPerSecond(likeRate).toFixed(1)}`}
                      />
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0">10</span>
                    </div>
                  </div>

                  {/* Share Rate */}
                  <div className={`flex flex-col gap-2 ${!isAutoChatEnabled ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">Share Rate</span>
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {getMessagesPerSecond(shareRate).toFixed(1)}/s
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0">0</span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={shareRate}
                        onChange={(e) => setShareRate(Number(e.target.value))}
                        disabled={!bridgeReady || !isAutoChatEnabled}
                        className="flex-1 h-2 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
                        style={{
                          accentColor: '#10b981'
                        }}
                        title={`Shares per second: ${getMessagesPerSecond(shareRate).toFixed(1)}`}
                      />
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0">10</span>
                    </div>
                  </div>

                  {/* Follow Rate */}
                  <div className={`flex flex-col gap-2 ${!isAutoChatEnabled ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">Follow Rate</span>
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {getMessagesPerSecond(followRate).toFixed(1)}/s
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0">0</span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={followRate}
                        onChange={(e) => setFollowRate(Number(e.target.value))}
                        disabled={!bridgeReady || !isAutoChatEnabled}
                        className="flex-1 h-2 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
                        style={{
                          accentColor: '#10b981'
                        }}
                        title={`Follows per second: ${getMessagesPerSecond(followRate).toFixed(1)}`}
                      />
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0">10</span>
                    </div>
                  </div>

                  {/* Join Rate */}
                  <div className={`flex flex-col gap-2 ${!isAutoChatEnabled ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-900 dark:text-white">Join Rate</span>
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {getMessagesPerSecond(joinRate).toFixed(1)}/s
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0">0</span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={joinRate}
                        onChange={(e) => setJoinRate(Number(e.target.value))}
                        disabled={!bridgeReady || !isAutoChatEnabled}
                        className="flex-1 h-2 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
                        style={{
                          accentColor: '#10b981'
                        }}
                        title={`Joins per second: ${getMessagesPerSecond(joinRate).toFixed(1)}`}
                      />
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0">10</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Stats Dropdown */}
          <div className="relative flex-shrink-0" ref={statsDropdownRef}>
            <button
              onClick={() => setShowStatsDropdown(!showStatsDropdown)}
              disabled={!bridgeReady}
              title="Stream statistics"
              className="flex items-center gap-2 px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-8"
            >
              <span className="text-xs font-medium">Stats</span>
              <ChevronDown className={`w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform ${
                showStatsDropdown ? 'rotate-180' : ''
              }`} />
            </button>
            
            {/* Stats Dropdown */}
            {showStatsDropdown && (
              <div className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-300 dark:border-gray-600 z-[60] p-3 min-w-[200px]">
                <div className="flex flex-col gap-3">
                  {/* Likes */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-900 dark:text-white">Likes</label>
                    <input
                      type="number"
                      value={statsLikes}
                      onChange={(e) => setStatsLikes(Number(e.target.value))}
                      onWheel={(e) => {
                        e.preventDefault();
                        const delta = e.deltaY > 0 ? -1 : 1;
                        setStatsLikes(Math.max(0, statsLikes + delta));
                      }}
                      min="0"
                      disabled={!bridgeReady}
                      className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1.5 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 cursor-ns-resize"
                    />
                  </div>
                  
                  {/* Viewers */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-900 dark:text-white">Viewers</label>
                    <input
                      type="number"
                      value={statsViewers}
                      onChange={(e) => setStatsViewers(Number(e.target.value))}
                      onWheel={(e) => {
                        e.preventDefault();
                        const delta = e.deltaY > 0 ? -1 : 1;
                        setStatsViewers(Math.max(0, statsViewers + delta));
                      }}
                      min="0"
                      disabled={!bridgeReady}
                      className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1.5 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 cursor-ns-resize"
                    />
                  </div>
                  
                  {/* Shares */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-900 dark:text-white">Shares</label>
                    <input
                      type="number"
                      value={statsShares}
                      onChange={(e) => setStatsShares(Number(e.target.value))}
                      onWheel={(e) => {
                        e.preventDefault();
                        const delta = e.deltaY > 0 ? -1 : 1;
                        setStatsShares(Math.max(0, statsShares + delta));
                      }}
                      min="0"
                      disabled={!bridgeReady}
                      className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1.5 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 cursor-ns-resize"
                    />
                  </div>
                  
                  {/* Gifts */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-900 dark:text-white">Gifts</label>
                    <input
                      type="number"
                      value={statsGifts}
                      onChange={(e) => setStatsGifts(Number(e.target.value))}
                      onWheel={(e) => {
                        e.preventDefault();
                        const delta = e.deltaY > 0 ? -1 : 1;
                        setStatsGifts(Math.max(0, statsGifts + delta));
                      }}
                      min="0"
                      disabled={!bridgeReady}
                      className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1.5 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 cursor-ns-resize"
                    />
                  </div>
                  
                  {/* Comments */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-900 dark:text-white">Comments</label>
                    <input
                      type="number"
                      value={statsComments}
                      onChange={(e) => setStatsComments(Number(e.target.value))}
                      onWheel={(e) => {
                        e.preventDefault();
                        const delta = e.deltaY > 0 ? -1 : 1;
                        setStatsComments(Math.max(0, statsComments + delta));
                      }}
                      min="0"
                      disabled={!bridgeReady}
                      className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1.5 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 cursor-ns-resize"
                    />
                  </div>
                  
                  {/* Followers */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-900 dark:text-white">Followers</label>
                    <input
                      type="number"
                      value={statsFollowers}
                      onChange={(e) => setStatsFollowers(Number(e.target.value))}
                      onWheel={(e) => {
                        e.preventDefault();
                        const delta = e.deltaY > 0 ? -1 : 1;
                        setStatsFollowers(Math.max(0, statsFollowers + delta));
                      }}
                      min="0"
                      disabled={!bridgeReady}
                      className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1.5 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 cursor-ns-resize"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Clear Button */}
          <button
            onClick={() => setChatMessages([])}
            disabled={!bridgeReady || chatMessages.length === 0}
            title="Clear chat messages"
            className="px-2 py-1.5 rounded text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Chat Feed - Scrollable */}
      <div 
        ref={chatFeedRef}
        className="flex-1 overflow-y-auto p-3 flex flex-col relative"
      >
        {/* Toast Notification - Positioned at top of chat */}
        {currentToast && (
          <div className="sticky top-0 left-0 right-0 mb-2 z-50 toast-enter">
            <div className={`flex items-center gap-2 p-2 rounded-lg shadow-lg ${
              currentToast.type === 'like' 
                ? 'bg-pink-500 dark:bg-pink-600' 
                : 'bg-blue-500 dark:bg-blue-600'
            }`}>
              <img 
                src={currentToast.avatarUrl} 
                alt={currentToast.username}
                className="w-6 h-6 rounded-full flex-shrink-0 border-2 border-white"
              />
              <span className="text-white text-sm font-medium flex-1 truncate">
                {currentToast.username}
              </span>
              <span className="text-white text-lg flex-shrink-0">
                {currentToast.type === 'like' ? '❤️' : '➜'}
              </span>
            </div>
          </div>
        )}

        {chatMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500 text-sm">
              No messages yet. Send a comment to get started!
            </div>
          </div>
        ) : (
          <>
            {/* Spacer to push messages to bottom */}
            <div className="flex-1 min-h-0" />
            <div className="space-y-2">
              {chatMessages.map(message => {
                const getMessageStyle = () => {
                  switch (message.type) {
                    case 'chat':
                      return 'bg-gray-200 dark:bg-gray-700';
                    case 'gift':
                      return 'bg-yellow-100 dark:bg-yellow-900 bg-opacity-30 dark:bg-opacity-30';
                    case 'follow':
                      return 'bg-blue-100 dark:bg-blue-900 bg-opacity-30 dark:bg-opacity-30';
                    case 'join':
                      return 'bg-green-100 dark:bg-green-900 bg-opacity-30 dark:bg-opacity-30';
                    default:
                      return 'bg-gray-200 dark:bg-gray-700';
                  }
                };
                
                // Compact rendering for follow and join messages
                if (message.type === 'follow' || message.type === 'join') {
                  return (
                    <div
                      key={message.id}
                      className={`flex items-center gap-2 px-2 py-1 rounded ${getMessageStyle()}`}
                    >
                      <img 
                        src={message.avatarUrl} 
                        alt={message.username}
                        className="w-5 h-5 rounded-full flex-shrink-0 border border-gray-300 dark:border-gray-600"
                      />
                      <span className={`font-semibold text-xs ${getUsernameColor(message.username)}`}>
                        {message.username}
                      </span>
                      <span className="text-xs text-gray-600 dark:text-gray-300">
                        {message.text}
                      </span>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 ml-auto">
                        {message.timestamp.toLocaleTimeString('en-US', { 
                          hour: '2-digit', 
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  );
                }
                
                // Regular rendering for chat and gift messages
                return (
                <div
                  key={message.id}
                  className={`flex items-start gap-2 p-2 rounded-lg ${getMessageStyle()}`}
                >
                {/* Profile Picture */}
                <img 
                  src={message.avatarUrl} 
                  alt={message.username}
                  className="w-8 h-8 rounded-full flex-shrink-0 border border-gray-300 dark:border-gray-600"
                />
                
                {/* Message Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className={`font-semibold text-sm ${getUsernameColor(message.username)}`}>
                      {message.username}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {message.timestamp.toLocaleTimeString('en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <p className="text-gray-800 dark:text-gray-200 text-sm mt-0.5 leading-relaxed">
                    {message.text}
                  </p>
                </div>
              </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Gift/Interaction Buttons - Platform Specific */}
      <div className="p-3">
        {/* TikTok: Gift Panel (shown above buttons when toggled) */}
        {selectedPlatform === 'tiktok' && showGiftPanel && (
          <div className="flex items-center gap-2 mb-2">
            {currentGifts.map(gift => {
              const isPending = pendingGift && pendingGift.id === gift.id;
              const displayCount = isPending ? pendingGift.count : gift.count;
              const IconComponent = gift.icon;
              
              return (
                <button
                  key={gift.id}
                  onClick={() => handleGiftClick(gift)}
                  disabled={!bridgeReady}
                  className={`relative flex flex-col items-center justify-center rounded-lg p-2 transition-colors flex-1 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden ${
                    isPending 
                      ? 'bg-primary-600 hover:bg-primary-700' 
                      : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {/* Animated border progress indicator */}
                  {isPending && (
                    <svg 
                      key={pendingGift.animationKey}
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      viewBox="0 0 100 100"
                      style={{ transform: 'rotate(-90deg)' }}
                    >
                      <circle
                        cx="50"
                        cy="50"
                        r="48"
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="3"
                        strokeDasharray="301.59"
                        strokeDashoffset="0"
                        strokeLinecap="round"
                        style={{
                          animation: 'giftTimerProgress 1.5s linear forwards'
                        }}
                      />
                    </svg>
                  )}
                  
                  <IconComponent className={`w-5 h-5 relative z-10 ${isPending ? 'text-white' : 'text-gray-700 dark:text-gray-300'}`} />
                  <span className={`text-xs mt-0.5 relative z-10 ${
                    isPending ? 'text-white font-bold' : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    ×{displayCount}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          {/* TikTok: Gift Toggle + Heart + Share + Join + Follow */}
          {selectedPlatform === 'tiktok' && (
            <>
              <button
                onClick={() => setShowGiftPanel(!showGiftPanel)}
                disabled={!bridgeReady}
                className={`flex items-center justify-center rounded-lg p-2 transition-colors w-10 h-10 disabled:opacity-50 disabled:cursor-not-allowed ${
                  showGiftPanel
                    ? 'bg-primary-600 hover:bg-primary-700 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
                title="Toggle gifts"
              >
                <Gift className="w-5 h-5" />
              </button>
              <button
                onClick={handleLike}
                disabled={!bridgeReady}
                className="flex items-center justify-center bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-2 transition-colors w-10 h-10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Heart className="w-5 h-5 text-red-400 fill-red-400" />
              </button>
              <button
                onClick={handleShare}
                disabled={!bridgeReady}
                className="flex items-center justify-center bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-2 transition-colors w-10 h-10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Share2 className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              </button>
              <button
                onClick={handleJoin}
                disabled={!bridgeReady}
                className="flex items-center justify-center bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-2 transition-colors w-10 h-10 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Join live"
              >
                <LogIn className="w-5 h-5 text-green-400" />
              </button>
              <button
                onClick={handleFollow}
                disabled={!bridgeReady}
                className="flex items-center justify-center bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-2 transition-colors w-10 h-10 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Follow"
              >
                <UserPlus className="w-5 h-5 text-blue-400" />
              </button>
            </>
          )}

          {/* YouTube: Only Heart */}
          {selectedPlatform === 'youtube' && (
            <button
              onClick={handleLike}
              disabled={!bridgeReady}
              className="flex items-center justify-center bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-2 transition-colors w-full h-12 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Heart className="w-6 h-6 text-red-400 fill-red-400" />
            </button>
          )}

          {/* Twitch: Bit Gifts + Share */}
          {selectedPlatform === 'twitch' && (
            <>
              {currentGifts.map(gift => {
                const IconComponent = gift.icon;
                return (
                  <button
                    key={gift.id}
                    onClick={() => handleGiftClick(gift)}
                    disabled={!bridgeReady}
                    className="flex flex-col items-center justify-center bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-2 transition-colors flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <IconComponent className="w-5 h-5 text-purple-400" />
                    <span className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">×{gift.count}</span>
                  </button>
                );
              })}
              <button
                onClick={handleShare}
                disabled={!bridgeReady}
                className="flex items-center justify-center bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg p-2 transition-colors w-10 h-10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Share2 className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Comment Input */}
      <div className="p-3">
        <div className="flex items-center gap-2 min-w-0">
          <input
            type="text"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={bridgeReady ? "Enter Comment..." : "Waiting for SDK..."}
            disabled={!bridgeReady}
            className="flex-1 min-w-0 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            onClick={handleSendComment}
            disabled={!comment.trim() || !bridgeReady}
            className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

